// RuntimeReviewTable
// Renders the "Runtime Question Review Set" card — a table of real user
// questions from Mongo with per-row review controls (answer correct,
// source correct, expected entity, improvement note, save button).
// All state and handlers are owned by ConsoleTraining.tsx and passed in as props.

import React from 'react';
import { RuntimeQuestionReviewItem, TrainingEntityType } from '../../../types';
import { ReviewDraft, EntityOption } from './types';

interface RuntimeReviewTableProps {
  runtimeReviews: RuntimeQuestionReviewItem[];
  loadingRuntimeReviews: boolean;
  reviewDrafts: Record<string, ReviewDraft>;
  savingReviewId: string;
  expectedEntityOptions: Record<TrainingEntityType, EntityOption[]>;
  updateReviewDraft: (reviewId: string, patch: Partial<ReviewDraft>) => void;
  saveRuntimeReview: (reviewId: string) => Promise<void>;
  loadRuntimeReviews: () => Promise<void>;
}

const RuntimeReviewTable: React.FC<RuntimeReviewTableProps> = ({
  runtimeReviews,
  loadingRuntimeReviews,
  reviewDrafts,
  savingReviewId,
  expectedEntityOptions,
  updateReviewDraft,
  saveRuntimeReview,
  loadRuntimeReviews,
}) => {
  return (
    <div className="console-card">
      <div className="inline-header">
        <h2>Runtime Question Review Set</h2>
        <button type="button" onClick={() => void loadRuntimeReviews()}>
          Refresh Runtime Reviews
        </button>
      </div>
      <p>Review real user question outcomes from Mongo and mark answer/source correctness.</p>
      {loadingRuntimeReviews ? <p>Loading runtime reviews...</p> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Asked At</th>
              <th>User</th>
              <th>Question</th>
              <th>Predicted Source</th>
              <th>Review</th>
            </tr>
          </thead>
          <tbody>
            {runtimeReviews.map((row) => {
              const draft = reviewDrafts[row.review_id] || {
                is_answer_correct: false,
                is_source_correct: false,
                expected_entity_type: 'table' as TrainingEntityType,
                expected_entity_id: '',
                improvement_note: '',
              };
              const entityChoices = expectedEntityOptions[draft.expected_entity_type] || [];
              return (
                <tr key={row.review_id}>
                  <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</td>
                  <td>{row.user_email || '-'}</td>
                  <td>
                    <div style={{ minWidth: 260 }}>
                      <strong>{row.question_text}</strong>
                      {row.llm_answer ? (
                        <details style={{ marginTop: '0.5rem' }}>
                          <summary>LLM Answer</summary>
                          <div className="column-values">{row.llm_answer}</div>
                        </details>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    {(row.predicted_entity_type || '-')} / {(row.predicted_entity_id || '-')}
                  </td>
                  <td>
                    <div className="column-fields-stack" style={{ minWidth: 260 }}>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={draft.is_answer_correct}
                          onChange={(e) =>
                            updateReviewDraft(row.review_id, { is_answer_correct: e.target.checked })
                          }
                        />
                        Answer Correct
                      </label>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={draft.is_source_correct}
                          onChange={(e) =>
                            updateReviewDraft(row.review_id, { is_source_correct: e.target.checked })
                          }
                        />
                        Source Correct
                      </label>
                      <label>
                        Expected Entity Type
                        <select
                          value={draft.expected_entity_type}
                          onChange={(e) => {
                            const nextType = e.target.value as TrainingEntityType;
                            const nextChoices = expectedEntityOptions[nextType] || [];
                            updateReviewDraft(row.review_id, {
                              expected_entity_type: nextType,
                              expected_entity_id: nextChoices[0]?.id || '',
                            });
                          }}
                        >
                          <option value="table">Table</option>
                          <option value="dashboard">Dashboard</option>
                        </select>
                      </label>
                      <label>
                        Expected Entity
                        <select
                          value={draft.expected_entity_id}
                          onChange={(e) =>
                            updateReviewDraft(row.review_id, { expected_entity_id: e.target.value })
                          }
                        >
                          <option value="">Select entity</option>
                          {entityChoices.map((item) => (
                            <option key={`${row.review_id}-${item.id}`} value={item.id}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Improvement Note
                        <textarea
                          rows={3}
                          value={draft.improvement_note}
                          onChange={(e) =>
                            updateReviewDraft(row.review_id, { improvement_note: e.target.value })
                          }
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => void saveRuntimeReview(row.review_id)}
                        disabled={savingReviewId === row.review_id}
                      >
                        {savingReviewId === row.review_id ? 'Saving...' : 'Save Review'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!runtimeReviews.length && !loadingRuntimeReviews ? (
              <tr>
                <td colSpan={5}>
                  <div>
                    <p>No runtime review rows found in Mongo.</p>
                    <p>Expected mock document shape:</p>
                    <pre style={{ whiteSpace: 'pre-wrap' }}>
{`{
  "review_id": "uuid",
  "user_email": "analyst@jobsohio.com",
  "question_text": "How many projects ...?",
  "llm_answer": "....",
  "predicted_entity_type": "table|dashboard",
  "predicted_entity_id": "12|dashboard-id",
  "created_at": "2026-02-20T00:00:00Z"
}`}
                    </pre>
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RuntimeReviewTable;